<template>
  <div class="admin-account">
    <div v-if="!isAdmin" class="blocked-state">
      <v-icon size="56" color="#EA5545">mdi-lock-alert</v-icon>
      <p>{{ t('adminAccount.noAccess') }}</p>
    </div>
    <div v-else>
      <header class="page-header">
        <div>
          <p class="eyebrow">{{ t('adminAccount.eyebrow') }}</p>
          <h1>{{ t('adminAccount.title') }}</h1>
          <p class="subtitle">{{ t('adminAccount.subtitle') }}</p>
        </div>
      </header>

      <div class="grid">
        <v-card class="profile-card" elevation="1">
          <v-card-title class="card-title">
            <v-icon left color="#EA5545">mdi-card-account-details</v-icon>
            {{ t('adminAccount.profile.title') }}
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="saveProfile">
              <div class="form-grid">
                <v-text-field
                  v-model="profileForm.user_vorname"
                  :label="t('adminAccount.fields.firstName')"
                  :error-messages="profileErrors.user_vorname"
                  @input="clearError('user_vorname')"
                  required
                />
                <v-text-field
                  v-model="profileForm.user_name"
                  :label="t('adminAccount.fields.lastName')"
                  :error-messages="profileErrors.user_name"
                  @input="clearError('user_name')"
                  required
                />
                <v-text-field
                  v-model="profileForm.user_admin_name"
                  :label="t('adminAccount.fields.adminName')"
                />
                <v-text-field
                  v-model="profileForm.user_email"
                  :label="t('adminAccount.fields.email')"
                  :error-messages="profileErrors.user_email"
                  @input="clearError('user_email')"
                  required
                />
                <v-text-field
                  v-model="profileForm.user_strasse"
                  :label="t('adminAccount.fields.street')"
                />
                <v-text-field
                  v-model="profileForm.user_ort"
                  :label="t('adminAccount.fields.city')"
                />
              </div>
              <div class="actions">
                <v-btn
                  color="#EA5545"
                  variant="flat"
                  :loading="isSavingProfile"
                  :disabled="isSavingProfile"
                  type="submit"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ t('adminAccount.profile.save') }}
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <v-card class="thumb-card" elevation="1">
          <v-card-title class="card-title">
            <v-icon left color="#EA5545">mdi-camera</v-icon>
            {{ t('adminAccount.avatar.title') }}
          </v-card-title>
          <v-card-text>
            <div class="thumb-section">
              <div class="thumb-preview">
                <img
                  v-if="thumbUrl"
                  :src="thumbUrl"
                  alt="Admin Avatar"
                />
                <div v-else class="thumb-placeholder">
                  <v-icon size="42" color="#999">mdi-account</v-icon>
                  <span>{{ t('adminAccount.avatar.empty') }}</span>
                </div>
              </div>
              <div class="thumb-actions">
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileChange"
                />
                <v-btn
                  color="#EA5545"
                  variant="outlined"
                  :loading="isUploading"
                  :disabled="isUploading"
                  @click="$refs.fileInput.click()"
                >
                  <v-icon left>mdi-upload</v-icon>
                  {{ t('adminAccount.avatar.upload') }}
                </v-btn>
                <v-btn
                  color="error"
                  variant="text"
                  :disabled="!hasThumb || isDeleting"
                  :loading="isDeleting"
                  @click="removeThumbnail"
                >
                  <v-icon left>mdi-delete</v-icon>
                  {{ t('adminAccount.avatar.remove') }}
                </v-btn>
              </div>
            </div>
            <v-alert
              v-if="uploadError"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              {{ uploadError }}
            </v-alert>
          </v-card-text>
        </v-card>

        <v-card class="password-card" elevation="1">
          <v-card-title class="card-title">
            <v-icon left color="#EA5545">mdi-lock-reset</v-icon>
            {{ t('adminAccount.password.title') }}
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="changePassword">
              <v-text-field
                v-model="passwordForm.password"
                :label="t('adminAccount.password.new')"
                type="password"
                :error-messages="passwordError"
                @input="passwordError = ''"
                required
              />
              <v-text-field
                v-model="passwordForm.confirm"
                :label="t('adminAccount.password.confirm')"
                type="password"
                :error-messages="passwordError"
                @input="passwordError = ''"
                required
              />
              <div class="actions">
                <v-btn
                  color="#EA5545"
                  variant="flat"
                  type="submit"
                  :loading="isSavingPassword"
                  :disabled="isSavingPassword"
                >
                  <v-icon left>mdi-lock-check</v-icon>
                  {{ t('adminAccount.password.save') }}
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { useUserStore } from '@/stores/userStore'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const successStore = inject('successStore')
const errorStore = inject('errorStore')
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const userStore = useUserStore()
const userData = computed(() => userStore?.userData || null)

const isAdmin = computed(() => Number(userData.value?.user_admin || 0) === 1)

const profileForm = reactive({
  user_vorname: '',
  user_name: '',
  user_admin_name: '',
  user_email: '',
  user_strasse: '',
  user_ort: ''
})

const profileErrors = reactive({})
const isSavingProfile = ref(false)

const passwordForm = reactive({
  password: '',
  confirm: ''
})
const passwordError = ref('')
const isSavingPassword = ref(false)

const fileInput = ref(null)
const isUploading = ref(false)
const isDeleting = ref(false)
const uploadError = ref('')

const thumbUrl = computed(() => {
  const hash = userData.value?.user_thumb
  return hash ? `/getfile/${hash}` : null
})

const hasThumb = computed(() => !!userData.value?.user_thumb)

function hydrateProfile() {
  if (!userData.value) return
  profileForm.user_vorname = userData.value.user_vorname || ''
  profileForm.user_name = userData.value.user_name || ''
  profileForm.user_admin_name = userData.value.user_admin_name || ''
  profileForm.user_email = userData.value.user_email || ''
  profileForm.user_strasse = userData.value.user_strasse || ''
  profileForm.user_ort = userData.value.user_ort || ''
}

watch(userData, () => {
  hydrateProfile()
}, { immediate: true })

function clearError(key) {
  if (profileErrors[key]) {
    profileErrors[key] = ''
  }
}

function validateProfile() {
  let valid = true
  const requiredFields = ['user_vorname', 'user_name', 'user_email']
  requiredFields.forEach((field) => {
    if (!profileForm[field]) {
      profileErrors[field] = t('adminAccount.errors.required')
      valid = false
    }
  })
  if (profileForm.user_email && !/^\S+@\S+\.\S+$/.test(profileForm.user_email)) {
    profileErrors.user_email = t('adminAccount.errors.email')
    valid = false
  }
  return valid
}

async function saveProfile() {
  if (!validateProfile()) return
  isSavingProfile.value = true
  try {
    const payload = {
      action: 'updateuser',
      ...profileForm
    }
    const response = await baseCalls.userPost(payload)
    if (response.data?.success) {
      userStore.setUser({
        ...(userData.value || {}),
        ...profileForm
      })
      successStore?.setSuccess?.(
        true,
        t('adminAccount.messages.profileSavedTitle'),
        t('adminAccount.messages.profileSavedText')
      )
    } else {
      throw new Error(response.data?.message || 'Update failed')
    }
  } catch (err) {
    console.error(err)
    errorStore?.setError?.(true, t('adminAccount.messages.profileError'))
  } finally {
    isSavingProfile.value = false
  }
}

async function changePassword() {
  if (!passwordForm.password || passwordForm.password.length < 6) {
    passwordError.value = t('adminAccount.errors.passwordLength')
    return
  }
  if (passwordForm.password !== passwordForm.confirm) {
    passwordError.value = t('adminAccount.errors.passwordMatch')
    return
  }
  isSavingPassword.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'updatepassword',
      password: passwordForm.password
    })
    if (response.data?.success) {
      successStore?.setSuccess?.(
        true,
        t('adminAccount.messages.passwordSavedTitle'),
        t('adminAccount.messages.passwordSavedText')
      )
      passwordForm.password = ''
      passwordForm.confirm = ''
    } else {
      throw new Error('Password not saved')
    }
  } catch (err) {
    console.error(err)
    passwordError.value = t('adminAccount.messages.passwordError')
  } finally {
    isSavingPassword.value = false
  }
}

function handleFileChange(event) {
  const file = event.target?.files?.[0]
  if (!file) return
  uploadThumbnail(file)
  event.target.value = ''
}

async function uploadThumbnail(file) {
  uploadError.value = ''
  isUploading.value = true
  const formData = new FormData()
  formData.append('files[]', file)
  formData.append('file_net_id', 'user_thumb')
  try {
    const response = await baseCalls.userUpload(formData)
    if (response.data?.success) {
      const thumbHash = response.data.thumb_hash ?? null
      if (thumbHash) {
        userStore.setUser({
          ...(userData.value || {}),
          user_thumb: thumbHash
        })
      }
      successStore?.setSuccess?.(
        true,
        t('adminAccount.messages.avatarUploadedTitle'),
        t('adminAccount.messages.avatarUploadedText')
      )
    } else {
      uploadError.value = response.data?.message || t('adminAccount.messages.avatarError')
    }
  } catch (err) {
    console.error(err)
    uploadError.value = t('adminAccount.messages.avatarError')
    errorStore?.setError?.(true, uploadError.value)
  } finally {
    isUploading.value = false
  }
}

async function removeThumbnail() {
  if (!hasThumb.value) return
  uploadError.value = ''
  isDeleting.value = true
  try {
    const response = await baseCalls.userPost({ action: 'deleteuserthumb' })
    if (response.data?.success) {
      userStore.setUser({
        ...(userData.value || {}),
        user_thumb: null
      })
      successStore?.setSuccess?.(
        true,
        t('adminAccount.messages.avatarRemovedTitle'),
        t('adminAccount.messages.avatarRemovedText')
      )
    } else {
      uploadError.value = response.data?.message || t('adminAccount.messages.avatarRemoveError')
    }
  } catch (err) {
    console.error(err)
    uploadError.value = t('adminAccount.messages.avatarRemoveError')
    errorStore?.setError?.(true, uploadError.value)
  } finally {
    isDeleting.value = false
  }
}
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminAccount.css"></style>
